/**
 * PDF Evidence Bundle UI V1
 *
 * Read-only UI surface to view/export/verify PDF evidence bundles.
 *
 * - No mutation
 * - No raw document text by default
 * - Show export hash
 * - Show verification pass/fail
 * - Show citations and advisory links
 * - Placeholder/unsupported artifacts excluded
 */

import React, { useState } from 'react';
import { exportPdfEvidence } from '../../trustgate/modules/workspace/connectors/pdf/pdfEvidenceExport';
import { verifyPdfEvidenceExport } from '../../trustgate/modules/workspace/connectors/pdf/pdfEvidenceVerifier';
import type { ContentArtifactV1 } from '../../trustgate/modules/workspace/ingestion/contentArtifact';
import type { ChunkLink } from '../../trustgate/modules/workspace/connectors/pdf/crossDocumentChunkLinker';
import type { PdfEvidenceExportV1, EvidenceCitation, ExportedCrossDocumentLink } from '../../trustgate/modules/workspace/connectors/pdf/pdfEvidenceExport';
import './PdfEvidenceBundle.css';

interface PdfEvidenceBundleProps {
  artifacts: ContentArtifactV1[];
  crossDocumentLinks: ChunkLink[];
}

export function PdfEvidenceBundle({ artifacts, crossDocumentLinks }: PdfEvidenceBundleProps) {
  const [exportData, setExportData] = useState<PdfEvidenceExportV1 | null>(null);
  const [verificationResult, setVerificationResult] = useState<any>(null);
  const [includeRawText, setIncludeRawText] = useState(false);

  // Filter out placeholder and unsupported artifacts
  const validArtifacts = artifacts.filter(
    a => a.metadata.extractionStatus === 'extracted'
  );

  const handleExport = () => {
    const exported = exportPdfEvidence(validArtifacts, crossDocumentLinks, {
      includeRawText
    });
    setExportData(exported);
    setVerificationResult(null); // Reset verification on new export
  };

  const handleVerify = () => {
    if (!exportData) return;
    const result = verifyPdfEvidenceExport(exportData);
    setVerificationResult(result);
  };

  const handleDownload = () => {
    if (!exportData) return;
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pdf-evidence-bundle-${exportData.exportId}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="pdf-evidence-bundle">
      <div className="pdf-evidence-bundle-header">
        <h2>PDF Evidence Bundle</h2>
        <div className="pdf-evidence-bundle-actions">
          <button onClick={handleExport} className="btn-primary">
            Export
          </button>
          {exportData && (
            <>
              <button onClick={handleVerify} className="btn-secondary">
                Verify
              </button>
              <button onClick={handleDownload} className="btn-secondary">
                Download
              </button>
            </>
          )}
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={includeRawText}
              onChange={e => setIncludeRawText(e.target.checked)}
            />
            Include raw text
          </label>
        </div>
      </div>

      {exportData && (
        <div className="pdf-evidence-bundle-content">
          <div className="pdf-evidence-bundle-info">
            <div className="info-item">
              <span className="info-label">Export ID:</span>
              <span className="info-value">{exportData.exportId}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Export Hash:</span>
              <span className="info-value code">{exportData.exportHash}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Exported At:</span>
              <span className="info-value">{new Date(exportData.exportedAt).toLocaleString()}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Version:</span>
              <span className="info-value">{exportData.exportVersion}</span>
            </div>
            {verificationResult && (
              <div className={`verification-status ${verificationResult.valid ? 'valid' : 'invalid'}`}>
                <span className="verification-label">Verification:</span>
                <span className="verification-value">
                  {verificationResult.valid ? '✓ PASS' : '✗ FAIL'}
                </span>
                {verificationResult.error && (
                  <div className="verification-error">{verificationResult.error}</div>
                )}
                {verificationResult.warnings && verificationResult.warnings.length > 0 && (
                  <div className="verification-warnings">
                    {verificationResult.warnings.map((warning: string, i: number) => (
                      <div key={i} className="warning">{warning}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="pdf-evidence-bundle-citations">
            <h3>Evidence Citations ({exportData.evidenceCitations.length})</h3>
            {exportData.evidenceCitations.length === 0 ? (
              <div className="empty-state">No citations found</div>
            ) : (
              <div className="citations-list">
                {exportData.evidenceCitations.map((citation: EvidenceCitation, index: number) => (
                  <div key={index} className="citation-item">
                    <div className="citation-header">
                      <span className="citation-artifact">{citation.artifactId}</span>
                      <span className="citation-hash code">{citation.artifactHash.slice(0, 12)}...</span>
                    </div>
                    <div className="citation-details">
                      <span className="citation-chunk">Chunk {citation.chunkIndex}</span>
                      <span className="citation-chunk-id code">{citation.chunkId}</span>
                    </div>
                    <div className="citation-excerpt">{citation.excerptPreview}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="pdf-evidence-bundle-links">
            <h3>Advisory Cross-Document Links ({exportData.crossDocumentLinks.length})</h3>
            {exportData.crossDocumentLinks.length === 0 ? (
              <div className="empty-state">No links found</div>
            ) : (
              <div className="links-list">
                {exportData.crossDocumentLinks.map((link: ExportedCrossDocumentLink, index: number) => (
                  <div key={index} className="link-item">
                    <div className="link-header">
                      <span className="link-type">{link.linkType}</span>
                      <span className="link-score">{(link.overlapScore * 100).toFixed(0)}%</span>
                    </div>
                    <div className="link-source">
                      <span className="link-label">Source:</span>
                      <span className="link-value code">{link.sourceArtifactHash.slice(0, 12)}...</span>
                      <span className="link-chunk">Chunk {link.sourceChunkIndex}</span>
                    </div>
                    <div className="link-target">
                      <span className="link-label">Target:</span>
                      <span className="link-value code">{link.targetArtifactHash.slice(0, 12)}...</span>
                      <span className="link-chunk">Chunk {link.targetChunkIndex}</span>
                    </div>
                    <div className="link-keywords">
                      <span className="link-label">Matched:</span>
                      <span className="link-keywords-list">
                        {link.matchedKeywords.join(', ')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {!exportData && (
        <div className="empty-state">
          Click Export to generate a PDF evidence bundle
        </div>
      )}
    </div>
  );
}
